/* eslint-disable indent */
import { useState } from 'react';

import Image from 'next/image';

import { Grid, TextField, ListItem, ListItemAvatar, Typography, colors } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LoadingButton from '@material-ui/lab/LoadingButton';

import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import { SectionHeader, IconAlternate, TypedText } from '@/src/components/molecules';
import { CardProduct } from '@/src/components/organisms';
import api from '@/src/lib/api';
import { MaskedTaxDocumentCPFCNPJ } from '@/src/lib/utils/masks';
import { Yup } from '@/src/lib/yup';

import OrderDetails from './OrderDetails';

const useStyles = makeStyles(theme => ({
  cardProduct: {
    [theme.breakpoints.up('md')]: {
      maxWidth: 400,
    },
  },
  media: {
    height: 250,
  },
  typed: {
    fontWeight: 'bold',
  },
  listItemAvatar: {
    minWidth: 28,
  },
  displayContainer: {
    marginTop: theme.spacing(1),
  },
}));

export default function TrackOrder({ className, ...rest }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [order, setOrder] = useState(null);
  const taxDocumentRegex =
    /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2})$|^(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})$/;

  const formik = useFormik({
    initialValues: {
      orderNumber: '',
      email: '',
      taxDocument: '',
    },
    validationSchema: Yup.object().shape(
      {
        orderNumber: Yup.string()
          .label('Número do pedido')
          .required('O número do pedido é obrigatório'),
        email: Yup.string()
          .label('E-mail')
          .email('É necessário um e-mail válido')
          .when('taxDocument', (taxDocument, currentSchema) =>
            taxDocument
              ? currentSchema
              : currentSchema.required('Precisamos do e-mail ou do documento utilizado no pedido'),
          ),
        taxDocument: Yup.string()
          .label('CPF/CNPJ')
          .when('email', (email, currentSchema) =>
            email
              ? currentSchema
              : currentSchema
                  .matches(taxDocumentRegex, 'Insira um CPF ou CNPJ válido')
                  .required('Precisamos do documento ou do e-mail utilizado no pedido'),
          ),
      },
      ['email', 'taxDocument'],
    ),
    onSubmit: (values, actions) => {
      actions.setSubmitting(true);
      const sanitizedTaxDocument =
        typeof values?.taxDocument === 'string' ? values.taxDocument.replace(/[^\d]/g, '') : '';
      return api
        .post(`/commons/orders/${values.orderNumber}/`, {
          orderNumber: values.orderNumber,
          email: values.email !== '' ? values.email : undefined,
          ...(sanitizedTaxDocument.length === 11 && { cpf: sanitizedTaxDocument }),
          ...(sanitizedTaxDocument.length === 14 && { cnpj: sanitizedTaxDocument }),
        })
        .then(response => setOrder(response.data))
        .catch(() => {
          enqueueSnackbar(
            'Nenhum pedido encontrado com as informações preenchidas. Verifique o número do pedido e se o e-mail ou CPF/CNPJ foram os mesmos utilizados no pedido.',
            { variant: 'error' },
          );
        })
        .finally(() => actions.setSubmitting(false));
    },
  });

  const handleClose = () => setOrder(null);

  return (
    <div className={className} {...rest}>
      <OrderDetails order={order} open={!!order} onClose={handleClose} />
      <Grid container spacing={4}>
        <Grid
          item
          container
          justifyContent="flex-start"
          alignItems="center"
          xs={12}
          md={6}
          data-aos="fade-up">
          <CardProduct
            className={classes.cardProduct}
            mediaClassName={classes.media}
            withShadow
            liftUp
            mediaContent={
              <Image
                src="/images/illustrations/home/orders.svg"
                alt="Acompanhe seu pedido"
                layout="fill"
                objectFit="contain"
              />
            }
            cardContent={
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <TextField
                      placeholder="951835086170-01"
                      label="Número do pedido"
                      size="medium"
                      name="orderNumber"
                      fullWidth
                      type="text"
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('orderNumber')}
                      error={formik.touched.orderNumber && Boolean(formik.errors.orderNumber)}
                      helperText={formik.touched.orderNumber && formik.errors.orderNumber}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      placeholder="E-mail utilizado na compra"
                      label="E-mail"
                      size="medium"
                      name="email"
                      fullWidth
                      type="email"
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('email')}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      placeholder="CPF ou CNPJ"
                      label="CPF/CNPJ"
                      size="medium"
                      name="taxDocument"
                      fullWidth
                      type="text"
                      disabled={formik.isSubmitting}
                      InputProps={{
                        inputComponent: MaskedTaxDocumentCPFCNPJ,
                      }}
                      {...formik.getFieldProps('taxDocument')}
                      error={formik.touched.taxDocument && Boolean(formik.errors.taxDocument)}
                      helperText={formik.touched.taxDocument && formik.errors.taxDocument}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      loading={formik.isSubmitting}
                      disabled={!formik.isValid}>
                      Acompanhar
                    </LoadingButton>
                  </Grid>
                </Grid>
              </form>
            }
          />
        </Grid>
        <Grid
          item
          container
          alignItems="center"
          alignContent="center"
          justifyContent="flex-end"
          xs={12}
          md={6}
          data-aos="fade-up">
          <SectionHeader
            label="Possui algum pedido em andamento?"
            title={
              <>
                <span>Acompanhe rapidamente</span>
                <br />
                <TypedText
                  component="span"
                  variant="h4"
                  color="primary"
                  className={classes.typed}
                  typedProps={{
                    strings: [
                      'suas entregas',
                      'pagamentos vencidos',
                      'pagamentos à vencer',
                      'reembolsos',
                    ],
                    typeSpeed: 100,
                    loop: true,
                  }}
                />
              </>
            }
            subtitle="Informe o número do seu pedido com seu e-mail ou CPF/CNPJ para ter acesso aos detalhes resumidos do status de atendimento."
            align="left"
            disableGutter
          />
          <Grid container className={classes.displayContainer}>
            {[
              'Verifique o pagamento',
              'Acompanhe a entrega',
              'Notas fiscais emitidas',
              'Solicitações de reembolso',
            ].map((item, index) => (
              <Grid item xs={6} key={index.toString()} data-aos="fade-up">
                <ListItem disableGutters>
                  <ListItemAvatar className={classes.listItemAvatar}>
                    <IconAlternate
                      size="extraSmall"
                      shape="circle"
                      fontIconClass="fas fa-check"
                      color={colors.green}
                    />
                  </ListItemAvatar>
                  <Typography variant="subtitle1" color="secondary" noWrap>
                    {item}
                  </Typography>
                </ListItem>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}

TrackOrder.propTypes = {
  className: PropTypes.string,
};
